/**
 * Subscription plans, limits and feature gates.
 *
 * Billing checkout is not integrated yet (no payment gateway). The plan model,
 * limits and per-feature gates are fully wired so the checkout UI can be
 * switched on later without touching business logic: `BILLING_UI_ENABLED` only
 * hides surfaces, it never disables enforcement.
 */

export const PLANS = {
  free: "free",
  pro: "pro",
} as const;

export type Plan = (typeof PLANS)[keyof typeof PLANS];

export const ALL_PLANS: readonly Plan[] = [PLANS.free, PLANS.pro];

export const DEFAULT_PLAN: Plan = PLANS.free;

export const LIMIT_UNLIMITED = null;

export interface PlanLimits {
  /** Maximum active products. `null` = unlimited. */
  maxProducts: number | null;
  /** Maximum orders created per workspace per calendar day. */
  maxOrdersPerDay: number | null;
  /** Maximum members (owner included). Invites count once active. */
  maxMembers: number | null;
  /** Maximum variants per product. `1` = single-variant products only. */
  maxVariantsPerProduct: number | null;
  /** Maximum stored invoice asset bytes per workspace. */
  maxStorageBytes: number | null;
}

export const PLAN_LIMITS: Record<Plan, PlanLimits> = {
  free: {
    maxProducts: 25,
    maxOrdersPerDay: 20,
    maxMembers: 1,
    maxVariantsPerProduct: 1,
    maxStorageBytes: 100 * 1024 * 1024,
  },
  pro: {
    maxProducts: LIMIT_UNLIMITED,
    maxOrdersPerDay: LIMIT_UNLIMITED,
    maxMembers: LIMIT_UNLIMITED,
    maxVariantsPerProduct: LIMIT_UNLIMITED,
    maxStorageBytes: LIMIT_UNLIMITED,
  },
};

export const BILLING_CYCLES = {
  monthly: "monthly",
  annual: "annual",
} as const;

export type BillingCycle = (typeof BILLING_CYCLES)[keyof typeof BILLING_CYCLES];

export const ALL_BILLING_CYCLES: readonly BillingCycle[] = [
  BILLING_CYCLES.monthly,
  BILLING_CYCLES.annual,
];

export const SUBSCRIPTION_STATUSES = {
  trialing: "trialing",
  active: "active",
  pastDue: "past_due",
  canceled: "canceled",
  expired: "expired",
} as const;

export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[keyof typeof SUBSCRIPTION_STATUSES];

export const PLAN_LABELS: Record<Plan, string> = {
  free: "Free",
  pro: "Pro",
};

export const PLAN_DESCRIPTIONS: Record<Plan, string> = {
  free: "Single user, up to 25 products and 20 orders a day.",
  pro: "Unlimited products, orders, members and custom variants.",
};

/** Display-only pricing. No gateway is wired; checkout stays hidden. */
export const PLAN_PRICING: Record<Plan, Record<BillingCycle, { amount: number; currency: string }>> = {
  free: {
    monthly: { amount: 0, currency: "IDR" },
    annual: { amount: 0, currency: "IDR" },
  },
  pro: {
    monthly: { amount: 149_000, currency: "IDR" },
    annual: { amount: 1_490_000, currency: "IDR" },
  },
};

export const TRIAL_PERIOD_DAYS = 14 as const;

export const BILLING_UI_ENABLED = false as const;

/* ------------------------------------------------------------------ features */

export const FEATURES = {
  multiUser: "multiUser",
  customVariants: "customVariants",
  advancedReports: "advancedReports",
  invoiceBranding: "invoiceBranding",
  unlimitedProducts: "unlimitedProducts",
  whatsappSharing: "whatsappSharing",
} as const;

export type Feature = (typeof FEATURES)[keyof typeof FEATURES];

export const PLAN_FEATURES: Record<Plan, readonly Feature[]> = {
  free: [FEATURES.whatsappSharing],
  pro: [
    FEATURES.multiUser,
    FEATURES.customVariants,
    FEATURES.advancedReports,
    FEATURES.invoiceBranding,
    FEATURES.unlimitedProducts,
    FEATURES.whatsappSharing,
  ],
};

export const ALL_FEATURES: readonly Feature[] = [
  FEATURES.multiUser,
  FEATURES.customVariants,
  FEATURES.advancedReports,
  FEATURES.invoiceBranding,
  FEATURES.unlimitedProducts,
  FEATURES.whatsappSharing,
];
